/**
 * Enumeration of all supported security models
 */
export enum OAuthTokenType {
  /**
   * Specifies that a local token should be retrieved
   */
  Local = 'local',
  /**
   * Specifies that a streaming token should be retrieved
   */
  Stream = 'streaming',
  /**
   * Specifies that the standard opaque token should be retrieved
   */
  Opaque = ''
}

/**
 * Represents the necessary configuration for an OpenID Connect provider
 */
export interface OidcConfig {
  /**
   * The application's client_id
   */
  clientId: string;
  /**
   * The pre-configured routing information for the application
   */
  routes: RouteConfig;
  /**
   * The scopes that should be requested if any
   */
  scopes?: string[];
  /**
   * Whether or not to automatically redirect the user to the login page
   */
  autoLogin?: boolean;
  /**
   * Whether or not to enable the silent-renew functionality
   */
  silentRenew?: boolean;
  /**
   * Sets the maximum clock offset that can exist between a token's issued at time and the local clock.
   * Exceeding this threshold will cause the token to fail validation
   */
  maxTimeOffsetSeconds?: number;
  /**
   * The url that stores the auto-configuration information for the IDP
   */
  autoConfigUrl: string;
  /**
   * Diagnostic configuration for the internal oidc client
   */
  diagnostics?: ClientDiagnostics;
  /**
   * Configures the csx token type to request
   */
  accessTokenTypeHint?: OAuthTokenType; 
  /**
  * The url that deletes the session from server
  */
  endSessionUrl?: string;
  /**
  * specify if csx security should handle the login errors
  */
  handleError?: boolean;
}

/**
 * Provides diagnostic configuration for the oidc client
 */
export interface ClientDiagnostics {
  /**
   * Whether or not the client should log warning messages
   */
  logWarning?: boolean;
  /**
   * Whether or not the client should log debug messages
   */
  logDebug?: boolean;
}

/**
 * The routes to use during normal functionality
 */
export interface RouteConfig {
  /**
   * Where to redirect the user when authentication is complete
   * Note: This is a white-listed url that needs to be configured on the IDP
   */
  redirectUrl: string;
  /**
   * The angular route to send users to if authentication fails
   */
  forbiddenRoute?: string;
  /**
   * The url to redirect the user to once they are logged out
   * Note: This is a white-listed url that needs to be configured on the IDP
   */
  logoutUrl?: string;
  /**
   * The url where the silent-renew page is located
   */
  silentRenewUrl?: string;
}
